import {
  ForgotPasswordApiRequest,
  ResetPasswordApiRequest,
  LoginApiRequest,
  LogoutApiRequest,
  EditPasswordApiRequest,
  VerificationAccountRequest,
  VerificationOTPRequest,
  DeviceAuthApiRequest,
  OtpRequest,
  SetupPasswordValidationApiRequest,
  SetupPasswordRequest,
  Login2faApiRequest,
  OtpRequestLogin2faApiRequest,
  OtpVerifySkipApiRequest,
  OtpVerifyLogin2faApiRequest
} from "@/data/payload/api/AuthRequest";
import {
  ForgotPasswordRequestInterface,
  ResetPasswordRequestInterface,
  LoginRequestInterface,
  LogoutRequestInterface,
  EditPasswordRequestInterface,
  VerificationAccountRequestInterface
} from "@/data/payload/contracts/AuthRequest";
import ApiService from "@/app/infrastructures/services/ApiService";
import { AuthDataMapper } from "@/data/persistences/mappers/AuthDataMapper";
import { AuthRepositoryInterface } from "@/data/persistences/repositories/contracts/AuthRepositoryInterface";
import {
  AuthEntities,
  SetupPasswordValidation,
  TokenVerificationAccountEntities,
  Login2faEntity,
  OtpRequestResponseEntity
} from "@/domain/entities/Auth";
import { Endpoints } from "@/app/infrastructures/misc/Endpoints";
import {
  ResponsePayload,
  ResponsePayloadV2
} from "@/domain/entities/ResponsePayload";
import { IS_SENDER_ACCOUNT } from "@/app/infrastructures/misc/Utils";

export class AuthApiRepository implements AuthRepositoryInterface {
  private service: ApiService;
  private mapper: AuthDataMapper;
  private endpoints: Endpoints;

  constructor(
    service: ApiService,
    mapper: AuthDataMapper,
    endpoints: Endpoints
  ) {
    this.service = service;
    this.mapper = mapper;
    this.endpoints = endpoints;
  }

  public async login(payload: LoginRequestInterface): Promise<AuthEntities> {
    const version = IS_SENDER_ACCOUNT ? "v1" : "v2";

    const resp = await this.service.invoke(
      "post",
      this.endpoints.login(version),
      undefined,
      payload as LoginApiRequest
    );
    return this.mapper.convertAuthDataFromApi(resp);
  }

  public async deviceAuth(payload: DeviceAuthApiRequest) {
    const resp = await this.service.invokeDeviceAuth({
      method: "post",
      url: this.endpoints.deviceAuth(),
      payload: payload as DeviceAuthApiRequest
    });
    return this.mapper.convertDeviceAuthDataFromApi(resp);
  }

  public async deviceAuthCheckApproval() {
    const resp = await this.service.invokeDeviceAuth({
      method: "get",
      url: this.endpoints.deviceAuthCheckApproval()
    });
    return this.mapper.convertDeviceAuthCheckDataFromApi(resp);
  }

  public async forgotPassword(
    payload: ForgotPasswordRequestInterface
  ): Promise<ResponsePayload> {
    const response = await this.service.invoke(
      "post",
      this.endpoints.forgotPassword(),
      {},
      payload as ForgotPasswordApiRequest
    );
    return this.mapper.convertForgotPasswordDataFromApi(response);
  }

  public async validateSetupPasswordLink(
    payload: SetupPasswordValidationApiRequest
  ): Promise<SetupPasswordValidation> {
    const response = await this.service.invoke(
      "post",
      this.endpoints.validateSetupPasswordLink(),
      {},
      payload
    );
    return this.mapper.convertValidateSetupPasswordLinkDataFromApi(response);
  }

  public async resendSetupPasswordLink(
    payload: SetupPasswordValidationApiRequest
  ): Promise<ResponsePayloadV2> {
    const response = await this.service.invoke(
      "post",
      this.endpoints.resendSetupPasswordLink(),
      {},
      payload
    );
    return this.mapper.convertResendSetupPasswordLinkDataFromApi(response);
  }

  public async resetPassword(
    payload: ResetPasswordRequestInterface
  ): Promise<ResponsePayload> {
    const response = await this.service.invoke(
      "patch",
      this.endpoints.resetPassword(),
      {},
      payload as ResetPasswordApiRequest
    );
    return this.mapper.convertResetPasswordDataFromApi(response);
  }

  public async editPassword(
    payload: EditPasswordRequestInterface,
  ): Promise<ResponsePayload> {
    const { version, ...items } = payload as EditPasswordApiRequest;
    const response = await this.service.invoke(
      "patch",
      this.endpoints.editPassword(version),
      {},
      payload as EditPasswordApiRequest
    );
    return this.mapper.convertEditPasswordDataFromApi(response);
  }

  public async logout(
    payload: LogoutRequestInterface
  ): Promise<ResponsePayload> {
    const response = await this.service.invoke(
      "post",
      this.endpoints.logout(),
      {},
      payload as LogoutApiRequest
    );
    return this.mapper.convertResetPasswordDataFromApi(response);
  }

  public async verificationAccount(
    payload: VerificationAccountRequestInterface
  ): Promise<TokenVerificationAccountEntities> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.verificationAccount(),
      {},
      payload as VerificationAccountRequest
    );
    return this.mapper.convertVerificationAcountDataFromApi(resp);
  }

  public async setupPassword(
    payload: SetupPasswordRequest
  ): Promise<ResponsePayload> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.setupPassword(),
      {},
      payload as SetupPasswordRequest
    );
    return this.mapper.convertSetupPasswordDataFromApi(resp);
  }

  public async requestOtp(
    payload: VerificationAccountRequestInterface
  ): Promise<TokenVerificationAccountEntities> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.requestOtp(),
      {},
      payload as OtpRequest
    );
    return this.mapper.convertResendOtpDataFromApi(resp);
  }

  public async verificationOTP(
    payload: VerificationOTPRequest
  ): Promise<boolean> {
    const resp = await this.service.invoke(
      "post",
      "/horde/v1/otp/verify",
      undefined,
      payload
    );
    return this.mapper.convertVerificationOTPDataFromApi(resp);
  }

  public async login2fa(payload: Login2faApiRequest): Promise<Login2faEntity> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.login2fa(),
      undefined,
      payload
    );
    return this.mapper.convertLogin2faFromApi(resp);
  }

  public async requestLogin2faOtp(payload: OtpRequestLogin2faApiRequest): Promise<OtpRequestResponseEntity> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.login2faOtpRequest(),
      undefined,
      payload
    );
    return this.mapper.convertLogin2faOtpRequestFromApi(resp);
  }

  public async verifyLogin2faSkip(payload: OtpVerifySkipApiRequest): Promise<AuthEntities> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.login2faOtpVerify(),
      undefined,
      payload
    );
    return this.mapper.convertLogin2faOtpVerifyFromApi(resp);
  }

  public async verifyLogin2fa(payload: OtpVerifyLogin2faApiRequest): Promise<AuthEntities> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.login2faOtpVerify(),
      undefined,
      payload
    );
    return this.mapper.convertLogin2faOtpVerifyFromApi(resp);
  }
}
